import { z } from 'zod'

export enum Provider {
  PRINTIFY = 'PRINTIFY',
  PRINTFUL = 'PRINTFUL',
}

const usedImageSchema = z.object({
  cropped: z.string(),
  original: z.string(),
})

const colorOptionSchema = z.object({
  label: z.string(),
  value: z.string(),
  maskImageUrl: z.string(),
})

const storybookEditStateSchema = z.object({
  currentSpreadIndex: z.number(),
  spreadVariables: z.record(z.string(), z.record(z.string(), z.string())),
  selectedCoverOption: z.string().nullish(),
})

export const productMetadataSchema = z.object({
  providerProductId: z.number(),
  variantId: z.number(),
  printProviderId: z.number().nullish().optional(),
  title: z.string(),
  price: z.number(),
  salePrice: z.number(),
  previewCdnUrl: z.string(),
  previewCdnUrls: z.array(z.string()),
  sourceImages: z.array(usedImageSchema),
  printAreas: z.array(
    z.object({
      position: z.enum([
        'front',
        'back',
        'left_leg',
        'right_leg',
        'front_1',
        'front_2',
        'front_3',
        'front_4',
        'first',
        'second',
        'third',
        'default',
        // Coffee print area
        'coffee_front',
        // Storybook print areas
        'cover',
        'spread_1',
        'spread_2',
        'spread_3',
        'spread_4',
        'spread_5',
        'spread_6',
        'spread_7',
        'spread_8',
        'spread_9',
        'spread_10',
      ]),
      imageUrl: z.string(),
    })
  ),
  designTemplateId: z.number().nullish(),
  pseudoTemplateId: z.string().nullish(),
  storybookTemplateId: z.number().nullish(),
  storybookEditState: storybookEditStateSchema.nullish(),
  selectedColor: colorOptionSchema.optional(),
})

export type PrintifyMetadata = z.infer<typeof productMetadataSchema>

export const addItemToCartPayloadSchema = z.object({
  itemIdentifier: z.string(),
  provider: z.nativeEnum(Provider),
  metadata: productMetadataSchema,
  cartType: z.string().optional(),
})

export type AddItemToCartPayload = z.infer<typeof addItemToCartPayloadSchema>

export type LineDiscount = {
  cartItemId: number
  isEligible: boolean
  discountedUnitsCount: number
  originalUnitPrice: number
  discountedUnitPrice: number
  perUnitSavings: number
  totalLineSavings: number
}

export type CartItem = {
  id: number
  cartId: number
  itemIdentifier: string
  provider: Provider
  quantity: number
  metadata: PrintifyMetadata
  price: number // Deprecated: kept for backward compatibility
  salePrice: number // Deprecated: kept for backward compatibility
  prices?: {
    usd?: { price: number; salePrice: number }
    cad?: { price: number; salePrice: number }
    gbp?: { price: number; salePrice: number }
  }
  createdAt: string
  updatedAt: string
  hasColors: boolean
  hasSizes: boolean
  category?: string | null
  discount?: LineDiscount
}

export type CartCampaignConfig = {
  discountPercent: number
  eligibleCategories: string[]
  isActive: boolean
}

/**
 * Flat-percentage promotional campaign (e.g. Grandparents' Day). Rates vary per
 * variant, so there is no single discountPercent — maxPercent is the ceiling,
 * for "Up To X% OFF" copy. Null when no such campaign is running.
 */
export type CartPromoCampaignConfig = {
  id: string
  isActive: boolean
  percentByCategory: Partial<Record<string, number>>
  maxPercent: number
}

export type Cart = {
  id: number
  familyAlbumUuid: string
  createdAt: string
  updatedAt: string
  items: CartItem[]
  abandonedAt: string | null
  resumedAt: string | null
  totalSavings?: number
  campaign?: CartCampaignConfig
  promoCampaign?: CartPromoCampaignConfig | null
}

export type GetCartResponse = Cart
export type AddItemToCartResponse = Cart
export type UpdateItemCartQuantityResponse = Cart
export type AddItemToCart = (payload: AddItemToCartPayload) => Promise<AddItemToCartResponse>

export type UpdateItemCartQuantity = (
  itemId: number,
  payload: {
    quantity: number
  }
) => Promise<UpdateItemCartQuantityResponse>
export type GetCart = () => Promise<GetCartResponse>
export type DeleteCartItem = (itemId: number) => Promise<Cart>

export type CreateCheckoutSession = () => Promise<{
  clientSecret: string
  checkoutSessionId: string
}>

export type GetCheckoutSession = (checkoutSessionId: string) => Promise<{
  amount: number
  email: string
}>

export type ResumeCart = () => Promise<Cart>
export type AbandonCart = () => void
